<template>
  <div class="member-bills-wrap">
    
    <!-- ── Summary Stats ────────────────────── -->
    <div class="stats-row mb-8">
      <PremiumStatCard
        label="Pending Bills"
        :value="bills.pending_count"
        icon="mdi-receipt-outline"
        color="#F59E0B"
        :loading="loading"
        :progress="bills.pending_count > 0 ? 40 : 100"
      />
      <PremiumStatCard
        label="Total Due"
        :value="'₹' + (bills.pending_amount || 0)"
        icon="mdi-currency-inr"
        color="#EF4444"
        :loading="loading"
        :progress="bills.pending_amount > 1000 ? 30 : 90"
      />
    </div>

    <v-row>
      <!-- ── Unpaid Invoices Table ────────────── -->
      <v-col cols="12" md="6">
        <div v-if="bills.pending_count > 0 || loading">
          <PremiumTable
            title="Unpaid Invoices"
            :columns="billColumns"
            :items="bills.pending"
            :loading="loading"
            :total-items="pendingTotal"
            :page.sync="pendingOptions.page"
            :items-per-page.sync="pendingOptions.itemsPerPage"
            :searchable="false"
          >
            <template #col-amount="{ item }">
              <span class="amount-text-red">₹{{ Number(item.amount).toLocaleString() }}</span>
            </template>

            <template #col-due_on="{ item }">
              <span class="date-text">{{ item.due_on | formatDate }}</span>
            </template>
          </PremiumTable>
        </div>

        <div v-else class="empty-state-premium">
          <div class="empty-icon-wrap">
            <div class="empty-icon-ring">
              <v-icon size="28" color="#10B981">mdi-check-decagram-outline</v-icon>
            </div>
          </div>
          <div class="empty-text-wrap">
            <h4 class="empty-title">All Caught Up!</h4>
            <p class="empty-sub">No pending dues found for this member.</p>
          </div>
        </div>
      </v-col>
      
      <!-- ── Payment Summary Table ────────────── -->
      <v-col cols="12" md="6">
        <PremiumTable
          title="Payment Summary (12 mo)"
          :columns="summaryColumns"
          :items="bills.month_wise || []"
          :loading="loading"
          :searchable="false"
          :show-footer="false"
        >
          <template #col-month="{ item }">
            <span class="month-text">{{ formatSummaryMonth(item.month) }}</span>
          </template>

          <template #col-paid_amount="{ item }">
            <span class="amount-text-green">₹{{ (item.paid_amount || 0).toLocaleString() }}</span>
          </template>

          <template #col-pending_amount="{ item }">
            <span :class="(item.pending_amount > 0) ? 'amount-text-red' : 'amount-text-gray'">
              ₹{{ (item.pending_amount || 0).toLocaleString() }}
            </span>
          </template>
        </PremiumTable>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import PremiumTable from '../../common/PremiumTable.vue';
import PremiumStatCard from '../../common/PremiumStatCard.vue';

export default {
  name: 'MemberBills',

  components: { PremiumTable, PremiumStatCard },

  props: {
    member_id: { type: [String, Number], required: true }
  },

  data() {
    return {
      loading: false,
      bills: {
        pending_count: 0,
        pending: [],
        month_wise: []
      },
      pendingTotal: 0,
      pendingOptions: {
        page: 1,
        itemsPerPage: 5
      },
      billColumns: [
        { key: 'title',  label: 'Bill Title' },
        { key: 'amount', label: 'Amount' },
        { key: 'due_on', label: 'Due Date', width: '140px' },
      ],
      summaryColumns: [
        { key: 'month',          label: 'Month' },
        { key: 'total_bills',    label: 'Bills', width: '80px' },
        { key: 'paid_amount',    label: 'Paid' },
        { key: 'pending_amount', label: 'Pending' },
      ]
    };
  },

  watch: {
    'pendingOptions.page'() { this.fetchBills(); },
    'pendingOptions.itemsPerPage'() { this.fetchBills(); }
  },

  created() {
    this.fetchBills();
  },

  methods: {
    async fetchBills() {
      this.loading = true;
      try {
        const response = await this.$axios.get(`societyadmin/members/${this.member_id}/bills`, {
          params: {
            page: this.pendingOptions.page,
            perPage: this.pendingOptions.itemsPerPage
          }
        });
        if (response.data.status) {
          this.bills = response.data.bills;
          this.pendingTotal = response.data.bills.total || 0;
        }
      } catch (error) {
        console.error("Error fetching bills:", error);
      } finally {
        this.loading = false;
      }
    },



    formatSummaryMonth(monthStr) {
      if (!monthStr) return '—';
      // Expecting YYYY-MM
      const parts = monthStr.split('-');
      if (parts.length < 2) return monthStr;
      const year = parts[0];
      const monthIdx = parseInt(parts[1]) - 1;
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${months[monthIdx]}-${year}`;
    }
  }
};
</script>

<style scoped>
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

.table-header-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.th-title-wrap { display: flex; align-items: center; }
.th-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 16px; font-weight: 700; color: var(--tx);
  letter-spacing: -0.02em; margin: 0;
}

/* ── Custom Styles ── */
.amount-text-red { font-weight: 700; color: #EF4444; font-family: 'JetBrains Mono', monospace; }
.amount-text-green { font-weight: 700; color: #10B981; font-family: 'JetBrains Mono', monospace; }
.amount-text-gray { font-weight: 500; color: var(--tx4); font-family: 'JetBrains Mono', monospace; }
.date-text { font-size: 13px; color: var(--tx2); font-weight: 600; white-space: nowrap; }
.month-text { font-weight: 700; color: var(--tx); }

/* ── Empty State ── */
.empty-state-premium {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  height: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
  padding: 40px 24px;
}
.empty-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}
.empty-icon-ring {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.08);
  border: 2px solid rgba(16, 185, 129, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}
.empty-text-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.empty-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: var(--tx);
  margin: 0;
}
.empty-sub {
  font-size: 13px;
  color: var(--tx3);
  margin: 0;
  font-weight: 500;
}
</style>
